/**
 * StatusPill — small colored chip for tag-style status callouts.
 *
 * The prototype's `.health-pill late|slow|partial|on-time`, the customer
 * detail's "Customer since…", "GSTIN verified", and similar tags all
 * collapse to one component. Variants pull from the brand spec's
 * sky/warn/bad/ok palette plus a neutral muted variant for "no signal".
 *
 * Distinct from `MetaPill` (already in primitives): `MetaPill` is a small
 * factual tag for hero / meta rows; `StatusPill` carries semantic colour
 * to communicate state (overdue, on time, archived, …).
 */
import * as React from "react";
import { cn } from "../lib/cn.js";

export type StatusTone = "muted" | "sky" | "warn" | "bad" | "ok";

export interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: StatusTone;
  /**
   * Pill size:
   * - `xs`: 10px uppercase, 0.04em letter-spacing, bold — matches the
   *   prototype's `.health-pill` exactly (`LATE · 32D` style).
   * - `sm`: 11px regular — chips on detail header ("Customer since …").
   * - `md`: 12px regular — wider tags in body cards.
   */
  size?: "xs" | "sm" | "md";
  /** Render a small dot before the label, matching the prototype's health pills. */
  dot?: boolean;
}

const TONE_STYLES: Record<StatusTone, { wrap: string; dot: string }> = {
  muted: { wrap: "bg-raised text-muted ring-1 ring-line", dot: "bg-muted" },
  sky: { wrap: "bg-accent-subtle text-accent-hover ring-1 ring-accent-subtle", dot: "bg-accent" },
  // AC91: text-warn (#ca8a04) on bg-warn-50 measured 2.84 contrast. Bumped
  // foreground to amber-800 (~#92400e) for >=4.5 WCAG-AA compliance.
  warn: { wrap: "bg-warn-50 text-amber-800 ring-1 ring-warn-100", dot: "bg-warn" },
  bad: { wrap: "bg-bad-50 text-bad ring-1 ring-bad-100", dot: "bg-bad" },
  ok: { wrap: "bg-ok-50 text-ok ring-1 ring-ok-100", dot: "bg-ok" },
};

const SIZE_STYLES: Record<NonNullable<StatusPillProps["size"]>, string> = {
  xs: "text-xs h-[18px] px-1.5 font-bold uppercase tracking-[0.04em]",
  sm: "text-xs h-5 px-1.5",
  md: "text-xs h-6 px-2",
};

export const StatusPill = React.forwardRef<HTMLSpanElement, StatusPillProps>(function StatusPill(
  { tone = "muted", size = "sm", dot, className, children, ...props },
  ref,
) {
  const styles = TONE_STYLES[tone];
  return (
    <span
      ref={ref}
      className={cn(
        // `whitespace-nowrap` because every size sets a fixed height: a label
        // that wraps puts its second line outside the capsule, which is what a
        // two-word status ("Below minimum") did in the products table's narrow
        // Status column. A pill is a single-line chip by definition, so this
        // makes the component honour the height it already declares.
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium leading-none",
        SIZE_STYLES[size],
        styles.wrap,
        className,
      )}
      {...props}
    >
      {dot ? (
        <span
          aria-hidden
          className={cn("rounded-full", size === "xs" ? "h-1 w-1" : "h-1.5 w-1.5", styles.dot)}
        />
      ) : null}
      {children}
    </span>
  );
});
